import type { ActionCreator } from '@reduxjs/toolkit';
import { OrderedSet, type RecordOf } from 'immutable';
import _merge from 'lodash/merge';
import queryString from 'query-string';
import type { Saga, SagaIterator, Task } from 'redux-saga';
import { type ActionPattern, call, cancel, delay, fork, put, race, select, spawn, take } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import { coreSettings, crashReporterInterface, getLogger, httpClientInterface } from '../../utils/core';
import { coreActions } from '../core-actions';
import { postponedActionsActions, postponedActionsHelpers } from '../postponed-actions';
import { undoActions, undoTypes } from '../undo';

import { cpMetaForResponseAction } from './actionHelpers';
import { type BaseResource, type QcsSyncResourcesPayloadProps, mkQcsSyncResourcesPayload } from './resourceStoreTypes';
import type { CoreAction, QcsSyncResourcesConfig } from './resourceStoreTypes';

const log = getLogger('data/qcsSyncResourceSagaHelpers');

function* onFailure<T extends BaseResource>(
  action: CoreAction<any, any>,
  error: any,
  config: QcsSyncResourcesConfig<T>,
  failureActionPayload: any,
): SagaIterator {
  const ignoreError =
    typeof config.ignoreError === 'function' ? config.ignoreError(error, action, config) : config.ignoreError;
  if (ignoreError !== true) {
    yield put(
      undoActions.addUndo(
        undoTypes.mkUndo({
          userMessage:
            typeof ignoreError === 'string' ? ignoreError : 'Sorry, but we were unable to make your changes.',
          key: uuidv4(),
        }),
      ),
    );
  }
  if (config.failureAction) {
    yield put(config.failureAction(failureActionPayload, cpMetaForResponseAction({ ...action.meta, config })));
  }
  action.meta?.reject?.(error);
}

function* handleError<T extends BaseResource>(
  action: CoreAction<any, any>,
  error: any,
  config: QcsSyncResourcesConfig<T>,
  failureActionPayload: any,
): SagaIterator {
  if (
    (typeof config.handleRetry === 'function' ? config.handleRetry(error, action, config) : config.handleRetry) &&
    postponedActionsHelpers.shouldPostponeAction(action, error)
  ) {
    yield put(
      postponedActionsActions.addPostponedAction({
        action,
        error,
        onFailure: function* _onFailure() {
          yield call(onFailure<T>, action, error, config, failureActionPayload);
        },
      }),
    );
  } else {
    yield call(onFailure<T>, action, error, config, failureActionPayload);
  }
}

const emptyAction = { type: '', meta: null, payload: null, asyncDispatch: undefined };

export function* qcsSyncGetResources<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  inAction: CoreAction<any, any>,
): SagaIterator {
  const action = inAction || emptyAction;
  try {
    const base = httpClientInterface.getServiceUrl();
    const resourceUrl =
      typeof config.resourceBaseUrl === 'function' ? config.resourceBaseUrl(action) : config.resourceBaseUrl;
    let url = new URL(resourceUrl, base);
    let requestUrl: string | undefined = url.href;

    const queryParams = queryString.parse(url.search);
    let lastSyncDate;
    if (config.getLastSyncDate) {
      lastSyncDate = yield select(config.getLastSyncDate);
    }

    const limitFF = yield select(featureFlagsSelectors.getFeatureFlag, 'limit');
    const limit = limitFF || 5000;
    const axiosConfig = {
      ..._merge(config.axiosConfig, action.meta?.axiosConfig),
      params: {
        ...(lastSyncDate && { modifiedAfter: lastSyncDate }),
        limit,
        ..._merge(config.axiosConfig?.params, action.meta?.axiosConfig?.params),
        ...queryParams,
      },
    };

    const actionSuccessPayload = {
      resources: [] as RecordOf<T>[],
      lastSyncDate: null,
      ...(!config.getLastSyncDate && { replaceCollection: true }),
    };

    while (requestUrl) {
      const response = yield call(
        httpClientInterface.send,
        'GET',
        requestUrl,
        undefined,
        axiosConfig.headers,
        axiosConfig.params,
      );

      // If this is the first page of data, update lastSyncDate with the asOf
      // time from the server so we can send it back the next time we retrieve
      // we retrieve resources.
      //
      requestUrl = undefined;
      if (response.data.metaData) {
        if (response.data.metaData.currentPage === 1) {
          actionSuccessPayload.lastSyncDate = response.data.metaData.asOf;
        }
        if (response.data.metaData.nextLink) {
          url = new URL(response.data.metaData.nextLink, base);
          requestUrl = url.href;
        }
      }

      let resources: RecordOf<T>[] | null | undefined;
      try {
        resources = config.transformResponseToResources(response);
      } catch (error) {
        crashReporterInterface.reportError(error);
        throw error;
      }

      actionSuccessPayload.resources = actionSuccessPayload.resources.concat(resources || []);
    }
    if (config.successAction) {
      yield put(
        config.successAction(
          mkQcsSyncResourcesPayload(actionSuccessPayload) as RecordOf<QcsSyncResourcesPayloadProps<T>>,
          cpMetaForResponseAction({ ...action.meta, config }),
        ),
      );
    }
    action.meta?.resolve?.(actionSuccessPayload.resources);
  } catch (error) {
    log.error(`Error retrieving resource "${config.resourceName}": ${error}`);
    if (config.failureAction) {
      yield put(config.failureAction(error, cpMetaForResponseAction({ ...action.meta, config })));
    }
    action.meta?.reject?.(error);

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    yield put(
      coreActions.setUpdateAllState({
        updateAllFailed: true,
        updateAllMessages: OrderedSet([`Can't load ${config.resourceName}.`]),
      }),
    );
/////////////
  }
}

export function* qcsSyncCreateResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: CoreAction<any, any>,
): SagaIterator {
  let response;

  const { payload: resource } = action;
  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource?.id ? `/${resource.id}` : ''}`;
  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  try {
    let requestData;
    try {
      requestData = config.transformResourceToRequestData(resource);
    } catch (error) {
      crashReporterInterface.reportError(error);
      throw error;
    }
    const axiosConfig = _merge(config.axiosConfig, action.meta?.axiosConfig);
    response = yield call(
      httpClientInterface.send,
      'POST',
      requestUrl,
      requestData,
      axiosConfig.headers,
      axiosConfig.params,
    );

    let resultingResource = response.data;
    if (config.transformResourceWithResponse !== null) {
      try {
        resultingResource = config.transformResourceWithResponse(resource, response, config);
      } catch (error) {
        crashReporterInterface.reportError(error);
        throw error;
      }
    }
    if (config.successAction) {
      yield put(config.successAction(resultingResource, cpMetaForResponseAction({ ...action.meta, config, response })));
    }
    // Note: This is a bit ugly, but early on we directly passed back response (no resource). Ideally we would have passed
    //       back new object, that contained resource and response but would require more refactoring.
    response.resource = resultingResource;
    action.meta?.resolve?.(response);
  } catch (error: any) {
    log.error(`Error creating ${config.resourceName}`, error);
    const payload = {
      clientId: resource?.clientId,
      ...error,
    };
    yield call(handleError<T>, action, error, config, payload);
    return error;
  }

  return response;
}

export type QCSRequestConfig = {
  method: 'GET' | 'PUT' | 'POST' | 'DELETE';
  path: string | ((action: CoreAction<any, any>) => string);
  data?: any | ((action: CoreAction<any, any>) => any);
  headers?: object | ((action: CoreAction<any, any>) => object);
  params?: object | ((action: CoreAction<any, any>) => object);
  successAction?: (payload?: any, meta?: any) => CoreAction<any, any>;
  failureAction?: (payload?: any, meta?: any) => CoreAction<any, any>;
  showError?: boolean | string | ((error: any, action: CoreAction<any, any>) => boolean | string);
};
export function* qcsRequest(config: QCSRequestConfig, action: any): SagaIterator {
  let response;

  const { method, path, data, headers, params, successAction, failureAction, showError = true } = config;

  const urlPath = typeof path === 'function' ? path(action) : path;
  const urlBase = httpClientInterface.getServiceUrl();
  const url = new URL(urlPath, urlBase);
  const requestData = typeof data === 'function' ? data(action) : data;
  const requestHeaders = typeof headers === 'function' ? headers(action) : headers;
  const requestParams = typeof params === 'function' ? params(action) : params;

  try {
    response = yield call(httpClientInterface.send, method, url.href, requestData, requestHeaders, requestParams);

    if (successAction) {
      yield put(successAction(response, cpMetaForResponseAction({ ...action.meta, config, response })));
    }

    action.meta?.resolve?.(response);
  } catch (error: any) {
    log.error(`Error ${method} ${urlPath}`, error);
    const resultError = typeof showError === 'function' ? showError(error, action) : showError;
    if (resultError) {
      yield put(
        undoActions.addUndo(
          undoTypes.mkUndo({
            userMessage: typeof showError === 'string' ? showError : 'Sorry, but we were unable to make your changes.',
            key: uuidv4(),
          }),
        ),
      );
    }
    if (failureAction) {
      yield put(failureAction(error, cpMetaForResponseAction({ ...action.meta, config })));
    }
    action.meta?.reject?.(error);
  }

  return response;
}

export function* qcsSyncUpdateResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: CoreAction<any, any>,
): SagaIterator {
  let response;

  const { payload: resource } = action;
  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource.id ? `/${resource.id}` : ''}`;
  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  try {
    let requestData;
    try {
      requestData = config.transformResourceToRequestData(resource);
    } catch (error) {
      crashReporterInterface.reportError(error);
      throw error;
    }
    const axiosConfig = _merge(config.axiosConfig, action.meta?.axiosConfig);
    response = yield call(
      httpClientInterface.send,
      'PUT',
      requestUrl,
      requestData,
      axiosConfig.headers,
      axiosConfig.params,
    );

    let resultingResource = response.data;
    if (config.transformResourceWithResponse !== null) {
      try {
        resultingResource = config.transformResourceWithResponse(resource, response, config);
      } catch (error) {
        crashReporterInterface.reportError(error);
        throw error;
      }
    }
    if (config.successAction) {
      yield put(config.successAction(resultingResource, cpMetaForResponseAction({ ...action.meta, config, response })));
    }
    response.resource = resultingResource;
    action.meta?.resolve?.(response);
  } catch (error: any) {
    log.error(`Error updating ${config.resourceName}`, error);
    const payload = {
      clientId: resource.clientId,
      ...error,
    };
    yield call(handleError<T>, action, error, config, payload);
  }

  return response;
}

export function* qcsSyncGetResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  inAction?: CoreAction<any, any>,
): SagaIterator {
  const action = inAction || emptyAction;
  const { payload: resource, asyncDispatch } = action;
  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource?.id ? `/${resource.id}` : ''}`;

  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  let response;
  try {
    const axiosConfig = _merge(config.axiosConfig, action.meta?.axiosConfig);
    response = yield call(
      httpClientInterface.send,
      'GET',
      requestUrl,
      undefined,
      axiosConfig.headers,
      axiosConfig.params,
    );

    let resources = [response.data];
    if (config.transformResponseToResource !== null) {
      try {
        resources = [config.transformResponseToResource(response)];
      } catch (error) {
        crashReporterInterface.reportError(error);
        throw error;
      }
    }
    if (config.successAction) {
      yield put(
        config.successAction(
          mkQcsSyncResourcesPayload({ resources, asyncDispatch }) as RecordOf<QcsSyncResourcesPayloadProps<T>>,
          cpMetaForResponseAction({ ...action.meta, config, response }),
        ),
      );
    }
    // Note: This is a bit ugly, but early on we directly passed back response (no resource). Ideally we would have passed
    //       back new object, that contained resource and response but would require more refactoring.
    response.resource = resources[0];
    action.meta?.resolve?.(response);
  } catch (error: any) {
    log.error(`Error getting ${config.resourceName}`, error);
    const payload = {
      id: resource ? resource.id : undefined,
      ...error,
    };
    if (config.failureAction) {
      yield put(config.failureAction(payload, cpMetaForResponseAction({ ...action.meta, config })));
    }
    action.meta?.reject?.(error);
    return payload;
  }

  return response;
}

export function* qcsSyncUpdateResources<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: CoreAction<any, any>,
): SagaIterator {
  const { payload: resources } = action;
  // TODO: add pagination support
  const base = httpClientInterface.getServiceUrl();
  const resourceUrl =
    typeof config.resourceBaseUrl === 'function' ? config.resourceBaseUrl(action) : config.resourceBaseUrl;
  const url = new URL(resourceUrl, base);
  const requestUrl = url.href;

  try {
    let requestData;
    try {
      requestData = config.transformResourcesToRequestData(resources);
    } catch (error) {
      crashReporterInterface.reportError(error);
      throw error;
    }
    const axiosConfig = _merge(config.axiosConfig, action.meta?.axiosConfig);
    const response = yield call(
      httpClientInterface.send,
      'PUT',
      requestUrl,
      requestData,
      axiosConfig.headers,
      axiosConfig.params,
    );

    let resultingResources = response.data;
    if (config.transformResourcesWithResponse !== null) {
      try {
        resultingResources = config.transformResourcesWithResponse(resources, response);
      } catch (error) {
        crashReporterInterface.reportError(error);
        throw error;
      }
    }
    if (config.successAction) {
      yield put(
        config.successAction(resultingResources, cpMetaForResponseAction({ ...action.meta, config, response })),
      );
    }
    action.meta?.resolve?.(response);
  } catch (error) {
    log.error(`Error batch ${config.resourceName}`, error);
    yield call(handleError<T>, action, error, config, error);
  }
}

// =================================================================================================
// Delete Resource
// =================================================================================================
export function* qcsSyncDeleteResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: CoreAction<any, any>,
): SagaIterator {
  const { payload: resource } = action || {};

  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource?.id ? `/${resource.id}` : ''}`;
  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  let response;
  try {
    const axiosConfig = _merge(config.axiosConfig, action.meta?.axiosConfig);
    response = yield call(
      httpClientInterface.send,
      'DELETE',
      requestUrl,
      undefined,
      axiosConfig.headers,
      axiosConfig.params,
    );

    let resultingResource = response.data;
    if (config.transformResourceWithResponse !== null) {
      try {
        resultingResource = config.transformResourceWithResponse(resource, response, config);
        resultingResource =
          'set' in resultingResource
            ? resultingResource.set('isDeleted', true)
            : {
                ...resultingResource,
                isDeleted: true,
              };
      } catch (error) {
        crashReporterInterface.reportError(error);
        throw error;
      }
    }

    if (config.successAction) {
      yield put(config.successAction(resultingResource, cpMetaForResponseAction({ ...action.meta, config, response })));
    }
    action.meta?.resolve?.(response);
  } catch (error: any) {
    log.error(`Error deleting ${config.resourceName}`, error);
    const payload = {
      id: resource ? resource.id : undefined,
      ...error,
    };
    yield call(handleError<T>, action, error, config, payload);
    return response;
  }

  return response;
}

export function* qcsSyncPollResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: CoreAction<any, any> = emptyAction,
): SagaIterator {
  const base = httpClientInterface.getServiceUrl();
  const resourceUrl =
    typeof config.resourceBaseUrl === 'function' ? config.resourceBaseUrl(action) : config.resourceBaseUrl;
  const url = new URL(resourceUrl, base);
  const requestUrl = url.href;

  let attempt;
  let response;
  for (attempt = 0; attempt < config.pollConfig.attempts; attempt += 1, yield delay(config.pollConfig.pause)) {
    try {
      const axiosConfig = _merge(config.axiosConfig, action.meta?.axiosConfig);
      response = yield call(
        httpClientInterface.send,
        'GET',
        requestUrl,
        undefined,
        axiosConfig.headers,
        axiosConfig.params,
      );

      if (config.pollConfig.isComplete(response, attempt)) {
        break;
      }
    } catch (error: any) {
      log.error(`Error polling ${config.resourceName}`, error);
      if (config.pollConfig.isComplete(error.response, attempt)) {
        break;
      }
    }
  }
  let resources;
  try {
    resources = config.transformResponseToResources(response);
  } catch (error) {
    crashReporterInterface.reportError(error);
    throw error;
  }
  if (attempt < config.pollConfig.attempts) {
    if (config.successAction) {
      yield put(
        config.successAction(
          mkQcsSyncResourcesPayload({ resources: resources || [] }) as RecordOf<QcsSyncResourcesPayloadProps<T>>,
          cpMetaForResponseAction({ ...action.meta, config, response }),
        ),
      );
    }
    action.meta?.resolve?.(response);
  } else {
    if (config.failureAction) {
      yield put(
        config.failureAction(
          mkQcsSyncResourcesPayload({ resources: resources || [] }) as RecordOf<QcsSyncResourcesPayloadProps<T>>,
          cpMetaForResponseAction({ ...action.meta, config, response }),
        ),
      );
    }
    action.meta?.reject?.(response);
  }
}

export function* takeAndDebounce(pattern: ActionPattern, saga: Saga, ...args: any[]): SagaIterator {
  let task;
  let debounced = 0;
  let executed = 0;
  while (true) {
    const action = yield take(pattern);
    const ms = coreSettings?.fetchDebounceTimeout ?? 0;
    if (!task) {
      executed += 1;
      log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, action);
      yield call(saga, ...args, action);
      task = yield fork(function* postponeHandling() {
        // eslint-disable-line no-loop-func
        yield delay(ms);
        task = undefined;
      });
    } else {
      debounced += 1;
      log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, action);
      yield cancel(task);
      task = yield fork(function* postponeHandling() {
        // eslint-disable-line no-loop-func
        yield delay(ms);
        debounced -= 1;
        executed += 1;
        log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, action);
        yield call(saga, ...args, action);
        task = undefined;
      });
    }
  }
}

export function createGreedDebounce(key: string) {
  let task: Task | undefined;
  let handled: boolean | undefined;
  let executed = 0;
  let debounced = 0;
  return {
    callFirst: function* callFirst(saga: Saga, ...args: any[]): SagaIterator {
      if (!task) {
        const ms = coreSettings?.fetchDebounceTimeout ?? 0;
        handled = true;
        executed += 1;
        log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, key);
        yield call(saga, ...args);
        task = yield spawn(function* taskSpawn() {
          yield delay(ms);
          task = undefined;
        });
      }
    },
    callLast: function* callLast<A extends any[]>(saga: Saga<A>, ...args: A): SagaIterator {
      if (task && !handled) {
        const ms = coreSettings?.fetchDebounceTimeout ?? 0;
        debounced += 1;
        log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, key);
        yield cancel(task);
        task = yield spawn(function* taskSpawn() {
          yield delay(ms);
          task = undefined;
          executed += 1;
          debounced -= 1;
          log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, key);
          yield call(saga, ...args);
        });
      }
      handled = false;
    },
  };
}

export function* fetchWait(
  resourceSelectors: any,
  successAction: ActionCreator<any>,
  failureAction: ActionCreator<any>,
  timeout = 3000,
) {
  const isLoading: boolean = yield select(resourceSelectors.getIsLoading);
  if (isLoading) {
    const { successResult } = yield race({
      successResult: take(successAction),
      failureResult: take(failureAction),
      timeoutResult: delay(timeout),
    });
    return Boolean(successResult);
  }
  return true;
}
